import { useState, useEffect } from "react";
import {
    createInvoiceFromDC,
    createInvoiceManual,
} from "../../services/api/billing/invoiceService";
import { fetchDcOuts } from "../../services/api/boq/dcOutApi";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const CreateBilling = () => {
    const [mode, setMode] = useState("dc"); // dc | manual

    // 🔷 DC MODE
    const [dcList, setDcList] = useState([]);
    const [dcId, setDcId] = useState("");
    const [stateType, setStateType] = useState("same");

    // 🔷 MANUAL MODE
    const [customerId, setCustomerId] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [items, setItems] = useState([
        { item_name: "", qty: 1, price: 0 },
    ]);

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadDcOuts();
    }, []);

    const loadDcOuts = async () => {
        try {
            const res = await fetchDcOuts();
            setDcList(res || []);
            console.log("DC Outs loaded:", res);
        } catch (err) {
            console.error("Error loading DC Outs", err);
        }
    };

    // ➕ Add Item
    const addItem = () => {
        setItems([...items, { item_name: "", qty: 1, price: 0 }]);
    };

    // ❌ Remove Item
    const removeItem = (index) => {
        const updated = [...items];
        updated.splice(index, 1);
        setItems(updated);
    };

    // ✏️ Update Item
    const handleItemChange = (index, field, value) => {
        const updated = [...items];
        updated[index][field] = value;
        setItems(updated);
    };

    // 💾 SUBMIT
    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (mode === "dc") {
                await createInvoiceFromDC({
                    dc_out_id: dcId,
                    state: stateType,
                });
            } else {
                await createInvoiceManual({
                    customer_id: customerId,
                    invoice_date: invoiceDate,
                    items: items,
                });
            }

            toast.success("Invoice created successfully");
            window.location.href = "/billing";
        } catch (err) {
            console.error(err);
            toast.error("Error creating invoice");
        }
        setLoading(false);
    };

    // 💰 TOTAL CALC
    const total = items.reduce(
        (sum, item) => sum + item.qty * item.price,
        0
    );

    return (
        <div className="p-4 max-w-4xl mx-auto">

            {/* HEADER */}
            <h2 className="text-2xl font-bold mb-4">Create Invoice</h2>

            {/* MODE SWITCH */}
            <div className="flex gap-4 mb-5">
                <button
                    onClick={() => setMode("dc")}
                    className={`px-4 py-2 rounded ${mode === "dc" ? "bg-blue-600 text-white" : "bg-gray-200"
                        }`}
                >
                    From DC
                </button>

                <button
                    onClick={() => setMode("manual")}
                    className={`px-4 py-2 rounded ${mode === "manual" ? "bg-blue-600 text-white" : "bg-gray-200"
                        }`}
                >
                    Manual
                </button>
            </div>

            {/* 🔷 DC MODE */}
            {mode === "dc" && (
                <div className="bg-white p-4 rounded shadow space-y-4">

                    <div>
                        <label className="block mb-1">DC Out ID</label>
                        <select
                            value={dcId}
                            onChange={(e) => setDcId(e.target.value)}
                            className="border p-2 w-full rounded"
                        >
                            <option value="">Select DC Out</option>

                            {dcList.map((dc) => (
                                <option key={dc.id} value={dc.id}>
                                    {dc.project_name || "Project"} - DC #{dc.id}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block mb-1">State</label>
                        <select
                            value={stateType}
                            onChange={(e) => setStateType(e.target.value)}
                            className="border p-2 w-full rounded"
                        >
                            <option value="same">Same State</option>
                            <option value="different">Different State</option>
                        </select>
                    </div>
                </div>
            )}

            {/* 🔷 MANUAL MODE */}
            {mode === "manual" && (
                <div className="bg-white p-4 rounded shadow space-y-4">

                    <div className="grid md:grid-cols-2 gap-4">
                        <input
                            type="number"
                            placeholder="Customer ID"
                            value={customerId}
                            onChange={(e) => setCustomerId(e.target.value)}
                            className="border p-2 rounded"
                        />

                        <input
                            type="date"
                            value={invoiceDate}
                            onChange={(e) => setInvoiceDate(e.target.value)}
                            className="border p-2 rounded"
                        />
                    </div>

                    {/* ITEMS */}
                    <div>
                        <h3 className="font-semibold mb-2">Items</h3>

                        {items.map((item, index) => (
                            <div
                                key={index}
                                className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2"
                            >
                                <input
                                    type="text"
                                    placeholder="Item Name"
                                    value={item.item_name}
                                    onChange={(e) =>
                                        handleItemChange(index, "item_name", e.target.value)
                                    }
                                    className="border p-2 rounded"
                                />

                                <input
                                    type="number"
                                    placeholder="Qty"
                                    value={item.qty}
                                    onChange={(e) =>
                                        handleItemChange(index, "qty", Number(e.target.value))
                                    }
                                    className="border p-2 rounded"
                                />

                                <input
                                    type="number"
                                    placeholder="Price"
                                    value={item.price}
                                    onChange={(e) =>
                                        handleItemChange(index, "price", Number(e.target.value))
                                    }
                                    className="border p-2 rounded"
                                />

                                <button
                                    onClick={() => removeItem(index)}
                                    className="bg-red-500 text-white rounded px-2"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={addItem}
                            className="bg-gray-700 text-white px-3 py-1 rounded mt-2"
                        >
                            + Add Item
                        </button>
                    </div>

                    {/* TOTAL */}
                    <div className="text-right font-bold text-lg">
                        Total: ₹ {total}
                    </div>
                </div>
            )}

            {/* SUBMIT */}
            <div className="mt-6 flex flex-col md:flex-row gap-3 md:justify-end">

                {/* Cancel Button */}
                <button
                    onClick={() => navigate(`/billing`)}
                    className="px-6 py-2 rounded border border-gray-400 text-gray-700 
               hover:bg-gray-100 transition duration-200 w-full md:w-auto"
                >
                    Cancel
                </button>

                {/* Submit Button */}
                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`px-6 py-2 rounded text-white w-full md:w-auto transition duration-200
      ${loading
                            ? "bg-green-300 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                >
                    {loading ? "Saving..." : "Create Invoice"}
                </button>

            </div>
        </div >
    );
};

export default CreateBilling;